import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  LayoutDashboard, Home, DollarSign, Plus, ChevronRight, ChevronDown, TrendingUp, Camera, Trash2, ArrowRight, User, History, Loader2, Database, Search, Sparkles, Phone, Mail, Edit2, Save, X, Calendar, Image as ImageIcon, FileText, Download, UploadCloud, FileUp, CheckCircle2, MessageSquare, Lock, ArrowUpRight, Receipt, Users, ClipboardList, Maximize2, AlertTriangle, Briefcase, IdCard, Building2, MapPin, Settings, ShieldCheck, Globe, BookOpen, Info, Paperclip
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { 
  Unit, Transaction, UnitStatus, UnitDocument, CommunicationEntry, TaxYearRecord, TaxPayment, EmploymentEntry, TaxInfo
} from './types';
import { 
  loadUnits, saveUnits, 
  loadTransactions, saveTransactions
} from './store';
import { getAIEstimate } from './services/gemini';

// --- Shared Sub-components ---

const Badge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    [UnitStatus.OCCUPIED]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    [UnitStatus.VACANT]: 'bg-slate-100 text-slate-700 border-slate-200',
    [UnitStatus.REPAIR]: 'bg-amber-100 text-amber-700 border-amber-200',
    [UnitStatus.DELINQUENT]: 'bg-rose-100 text-rose-700 border-rose-200',
    'Rent': 'bg-emerald-50 text-emerald-600 border-emerald-100',
    'Late Fee': 'bg-rose-50 text-rose-600 border-rose-100',
    'Utility': 'bg-blue-50 text-blue-600 border-blue-100',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const EditableField = ({ 
  label, value, icon: Icon, isEditing, onChange, type = "text", placeholder = ""
}: { 
  label: string, value: any, icon: any, isEditing: boolean, onChange: (val: any) => void, type?: string, placeholder?: string
}) => (
  <div className="space-y-1 w-full">
    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
      <Icon size={12} /> {label}
    </label>
    {isEditing ? (
      <input 
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 font-bold transition-all text-sm"
      />
    ) : (
      <p className="font-bold text-slate-900 bg-slate-50/50 px-4 py-3 rounded-xl border border-transparent truncate text-sm min-h-[46px] flex items-center" title={String(value)}>
        {type === 'number' && typeof value === 'number' ? `$${value.toLocaleString()}` : (value || '—')}
      </p>
    )}
  </div>
);

// --- Documentation Page Components ---

const PortfolioDoc = () => (
  <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl shadow-blue-50/20 animate-fadeIn">
    <div className="flex items-center gap-4 mb-10 pb-6 border-b border-slate-50">
      <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
        <Home size={24} />
      </div>
      <div>
        <h3 className="text-2xl font-black text-slate-900">Portfolio Management Doc</h3>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Functional Specification v1.0</p>
      </div>
    </div>
    
    <div className="space-y-12">
      <section className="space-y-4">
        <h4 className="text-lg font-black text-slate-800 flex items-center gap-3">
          <span className="w-6 h-6 bg-slate-900 text-white text-[10px] rounded-full flex items-center justify-center font-bold">1</span>
          System Overview
        </h4>
        <div className="ml-9 space-y-4">
          <p className="text-slate-600 leading-relaxed font-medium">
            The Portfolio Manager is the core operational dashboard for Category One Properties. It provides a comprehensive, digital twin of the mobile home park, allowing for detailed tracking of occupancy, asset condition, tenant demographics, and document compliance for all 10 units.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
             <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-xs font-black uppercase text-slate-400 mb-2">Asset Tracking</p>
                <p className="text-sm font-bold text-slate-700">Monitor Make, Model, Year, and physical condition notes for every lot.</p>
             </div>
             <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-xs font-black uppercase text-slate-400 mb-2">Tenant Relations</p>
                <p className="text-sm font-bold text-slate-700">Centralized database for contact info, employment history, and household income verification.</p>
             </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h4 className="text-lg font-black text-slate-800 flex items-center gap-3">
          <span className="w-6 h-6 bg-slate-900 text-white text-[10px] rounded-full flex items-center justify-center font-bold">2</span>
          Data Dictionary
        </h4>
        <div className="ml-9 space-y-2">
           <p className="text-slate-600 font-medium mb-4">Key data entities managed within the Unit Detail View:</p>
           <div className="grid grid-cols-1 gap-3">
              {[
                { name: 'Unit Status', desc: 'Enum: Occupied, Vacant, Under Repair, Delinquent. Drives visual badges and summary statistics.' },
                { name: 'Lease Terms', desc: 'Includes Move-In Date, Lease Start, and Payoff Date (Lease End). Critical for occupancy planning.' },
                { name: 'Household Income', desc: 'Calculated Sum of "Monthly Income" from all Employment History entries.' },
                { name: 'Documents', desc: 'Base64 encoded files (PDF/Images) stored in the "Document Vault".' },
                { name: 'Communication Log', desc: 'Timestamped notes for tracking tenant interactions, disputes, or agreements.' },
                { name: 'Tax Info', desc: 'County tax portal URL and payment records for 1st/2nd half of the year.' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                   <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                   <div>
                     <span className="font-black text-slate-800 text-sm">{item.name}:</span>
                     <span className="text-slate-500 text-sm font-medium ml-2">{item.desc}</span>
                   </div>
                </div>
              ))}
           </div>
        </div>
      </section>

      <section className="space-y-4">
        <h4 className="text-lg font-black text-slate-800 flex items-center gap-3">
          <span className="w-6 h-6 bg-slate-900 text-white text-[10px] rounded-full flex items-center justify-center font-bold">3</span>
          Operational Workflows
        </h4>
        <div className="ml-9 space-y-6">
           <div className="space-y-2">
             <h5 className="text-sm font-black uppercase text-slate-400 tracking-widest">New Acquisition / Setup</h5>
             <p className="text-sm font-medium text-slate-600">
                Use the <span className="text-blue-600 font-bold">"+ Add Property"</span> button on the main list. 
                Enter the Lot # and physical address immediately to establish the record. Set Status to "Vacant" initially.
             </p>
           </div>
           <div className="space-y-2">
             <h5 className="text-sm font-black uppercase text-slate-400 tracking-widest">Tenant Onboarding</h5>
             <p className="text-sm font-medium text-slate-600">
                1. Click "Edit Unit".<br/>
                2. Update Tenant Name, Phone, and Email.<br/>
                3. Add Employment entries to verify income coverage.<br/>
                4. Upload a photo of the Driver's License in the ID section.<br/>
                5. Upload the signed Lease Agreement to the Document Vault.
             </p>
           </div>
           <div className="space-y-2">
             <h5 className="text-sm font-black uppercase text-slate-400 tracking-widest">Maintenance & Inspections</h5>
             <p className="text-sm font-medium text-slate-600">
                Update the "Last Inspection" date after walkthroughs. Add specific repair needs to "Condition & Maintenance". 
                For complex repairs, use the "Repair AI" tab to estimate costs before updating the unit record.
             </p>
           </div>
        </div>
      </section>
    </div>
  </div>
);

const RentLedgerDoc = () => (
  <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl shadow-blue-50/20 animate-fadeIn">
    <div className="flex items-center gap-4 mb-10 pb-6 border-b border-slate-50">
      <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
        <BookOpen size={24} />
      </div>
      <div>
        <h3 className="text-2xl font-black text-slate-900">Rent Ledger Page Doc</h3>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Functional Specification v1.0</p>
      </div>
    </div>
    
    <div className="space-y-12">
      <section className="space-y-4">
        <h4 className="text-lg font-black text-slate-800 flex items-center gap-3">
          <span className="w-6 h-6 bg-slate-900 text-white text-[10px] rounded-full flex items-center justify-center font-bold">1</span>
          System Overview
        </h4>
        <div className="ml-9 space-y-4">
          <p className="text-slate-600 leading-relaxed font-medium">
            The Rent Ledger is the central financial tracking system for the Category One Properties portfolio. It aggregates all income streams (Rent, Utilities, Late Fees) into a unified, year-based view. Data is persisted securely via Supabase, ensuring real-time synchronization across all administrative devices.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
             <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-xs font-black uppercase text-slate-400 mb-2">Primary Goal</p>
                <p className="text-sm font-bold text-slate-700">Accurate cash-flow monitoring with "Net Received" calculations auto-deducting transaction fees.</p>
             </div>
             <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-xs font-black uppercase text-slate-400 mb-2">Data Integrity</p>
                <p className="text-sm font-bold text-slate-700">Records are immutable unless deleted by an Admin. Regular backups via Excel export are recommended.</p>
             </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h4 className="text-lg font-black text-slate-800 flex items-center gap-3">
          <span className="w-6 h-6 bg-slate-900 text-white text-[10px] rounded-full flex items-center justify-center font-bold">2</span>
          Data Dictionary & Columns
        </h4>
        <div className="ml-9 space-y-2">
           <p className="text-slate-600 font-medium mb-4">The ledger table consists of the following standardized fields:</p>
           <ul className="space-y-3">
              {[
                { name: 'Date Paid', desc: 'The actual date the funds were received or the transaction cleared.' },
                { name: 'MH / Lot', desc: 'The specific unit associated with the payment. Linked to the Portfolio database.' },
                { name: 'Payee (Resident)', desc: 'The primary tenant name on file at the time of payment.' },
                { name: 'Rent Paid', desc: 'The gross amount received from the tenant before any deductions.' },
                { name: 'PayPal Fee', desc: 'Transaction processing fees (e.g., Stripe, PayPal) deducted from the gross.' },
                { name: 'Net Received', desc: 'Calculated Field: (Rent Paid - PayPal Fee). This represents actual cash-in-hand.' }
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                   <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                   <div>
                     <span className="font-black text-slate-800 text-sm">{item.name}:</span>
                     <span className="text-slate-500 text-sm font-medium ml-2">{item.desc}</span>
                   </div>
                </li>
              ))}
           </ul>
        </div>
      </section>

      <section className="space-y-4">
        <h4 className="text-lg font-black text-slate-800 flex items-center gap-3">
          <span className="w-6 h-6 bg-slate-900 text-white text-[10px] rounded-full flex items-center justify-center font-bold">3</span>
          Operational Workflows
        </h4>
        <div className="ml-9 space-y-6">
           <div className="space-y-2">
             <h5 className="text-sm font-black uppercase text-slate-400 tracking-widest">Adding a Receipt</h5>
             <p className="text-sm font-medium text-slate-600">Click the <span className="text-blue-600 font-bold">"+ Record Receipt"</span> button to open the transaction modal. Select the Lot #, enter the Gross Amount, and explicitly state any Fees. The Net Amount is calculated automatically upon saving.</p>
           </div>
           <div className="space-y-2">
             <h5 className="text-sm font-black uppercase text-slate-400 tracking-widest">Excel Integration</h5>
             <p className="text-sm font-medium text-slate-600">
               <span className="font-bold text-slate-800">Export:</span> Generates a .xlsx file of the currently filtered year.<br/>
               <span className="font-bold text-slate-800">Import:</span> Allows bulk uploading of historical data. The importer expects columns named exactly: "Date Paid", "MH / Lot", "Rent Paid", and "Fee".
             </p>
           </div>
        </div>
      </section>
    </div>
  </div>
);

const DocumentationView = ({ activeDoc }: { activeDoc: string }) => {
  return (
    <div className="max-w-4xl mx-auto space-y-12 animate-fadeIn py-10">
      <div className="space-y-4">
        <h2 className="text-4xl font-black tracking-tight text-slate-900">Site Documentation</h2>
        <p className="text-lg text-slate-500 font-medium">Internal technical and functional specifications.</p>
      </div>
      {activeDoc === 'rent-ledger' && <RentLedgerDoc />}
      {activeDoc === 'portfolio' && <PortfolioDoc />}
      {activeDoc === 'none' && (
        <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl shadow-blue-50/20 text-center space-y-6">
          <div className="w-20 h-20 bg-slate-50 text-slate-300 rounded-[2rem] flex items-center justify-center mx-auto"><FileText size={40} /></div>
          <h3 className="text-xl font-black text-slate-900">Select an Article</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">Please choose a topic from the documentation menu.</p>
        </div>
      )}
    </div>
  );
};

// --- Unit Detail View Component ---

interface UnitDetailProps {
  unit: Unit;
  transactions: Transaction[];
  isNew?: boolean;
  onBack: () => void;
  onSave: (updatedUnit: Unit) => void;
  onDeleteUnit: (id: string) => void;
  onDeleteTransaction: (id: string) => void;
}

const UnitDetailView = ({ unit, transactions, isNew = false, onBack, onSave, onDeleteUnit, onDeleteTransaction }: UnitDetailProps) => {
  const [isEditing, setIsEditing] = useState(isNew);
  const [formData, setFormData] = useState<Unit>(unit);
  const [selectedTaxYear, setSelectedTaxYear] = useState(new Date().getFullYear());
  const [viewPhoto, setViewPhoto] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    setFormData(unit);
    if (isNew) setIsEditing(true);
  }, [unit, isNew]);

  const totalMonthlyIncome = useMemo(() => {
    return (formData.employmentHistory || []).reduce((acc, job) => acc + (job.monthlyIncome || 0), 0);
  }, [formData.employmentHistory]);

  const updateField = (key: keyof Unit, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const currentTaxRecord: TaxYearRecord = useMemo(() => {
    return formData.taxInfo?.records?.[selectedTaxYear] || {
      payment1: { amount: 0, date: '' },
      payment2: { amount: 0, date: '' },
      comments: ''
    };
  }, [formData.taxInfo, selectedTaxYear]);

  const updateTaxRecord = (update: Partial<TaxYearRecord>) => {
    const currentTaxInfo = formData.taxInfo || { records: {}, taxWebsite: '' };
    setFormData(prev => ({
      ...prev,
      taxInfo: {
        ...currentTaxInfo,
        records: {
          ...currentTaxInfo.records,
          [selectedTaxYear]: { ...currentTaxRecord, ...update }
        }
      }
    }));
  };

  const updateTaxPayment = (paymentKey: 'payment1' | 'payment2', field: keyof TaxPayment, value: any) => {
    updateTaxRecord({ [paymentKey]: { ...currentTaxRecord[paymentKey], [field]: value } });
  };

  const handleSave = () => {
    const cleanedNotes = (formData.conditionNotes || []).filter(n => n && n.trim() !== '');
    const cleanedLogs = (formData.communicationLog || []).filter(l => l.note && l.note.trim() !== '');
    onSave({ ...formData, conditionNotes: cleanedNotes, communicationLog: cleanedLogs });
    setIsEditing(false);
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => updateField('thumbnail', reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleLicenseUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => updateField('driversLicense', reader.result as string);
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setFormData(prev => ({ ...prev, photos: [...prev.photos, base64] }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const newDoc: UnitDocument = {
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: file.type,
        data: reader.result as string
      };
      setFormData(prev => ({ ...prev, documents: [...prev.documents, newDoc] }));
    };
    reader.readAsDataURL(file);
  };

  const handleAddJob = () => {
    const newJob: EmploymentEntry = {
      id: Math.random().toString(36).substr(2, 9), jobName: 'New Employer', tenantName: '', startDate: new Date().toISOString().split('T')[0], monthlyIncome: 0
    };
    setFormData(prev => ({ ...prev, employmentHistory: [...prev.employmentHistory, newJob] }));
  };

  const updateJob = (id: string, field: keyof EmploymentEntry, value: any) => {
    setFormData(prev => ({
      ...prev, employmentHistory: prev.employmentHistory.map(job => job.id === id ? { ...job, [field]: value } : job)
    }));
  };

  const handleAddLog = () => {
    const newLog: CommunicationEntry = { id: Math.random().toString(36).substr(2, 9), date: new Date().toISOString().split('T')[0], note: '' };
    setFormData(prev => ({ ...prev, communicationLog: [...(prev.communicationLog || []), newLog] }));
  };

  const updateLog = (id: string, field: keyof CommunicationEntry, value: string) => {
    setFormData(prev => ({ ...prev, communicationLog: (prev.communicationLog || []).map(l => l.id === id ? { ...l, [field]: value } : l) }));
  };

  const handleAddConditionNote = () => {
    if(!newNote.trim()) return;
    setFormData(prev => ({ ...prev, conditionNotes: [...prev.conditionNotes, newNote] }));
    setNewNote('');
  };

  const taxYearOptions = Array.from({ length: 15 }, (_, i) => 2020 + i);

  return (
    <div className="space-y-8 animate-fadeIn pb-20 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold transition-colors">
          <ArrowRight className="rotate-180" size={18} /> Back to Portfolio
        </button>
        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <button onClick={() => { if (isNew) onBack(); else { setIsEditing(false); setFormData(unit); } }} className="px-6 py-3 bg-slate-100 text-slate-600 rounded-2xl font-bold flex items-center gap-2 hover:bg-slate-200 transition-colors">
                <X size={18} /> Cancel
              </button>
              <button onClick={handleSave} className="px-6 py-3 bg-emerald-600 text-white rounded-2xl font-bold flex items-center gap-2 hover:bg-emerald-700 shadow-xl shadow-emerald-100 transition-colors">
                <Save size={18} /> Save Changes
              </button>
            </>
          ) : (
            <div className="flex items-center gap-3">
              {isConfirmingDelete ? (
                <div className="flex items-center gap-2 bg-rose-50 p-2 rounded-2xl border border-rose-100 animate-scaleIn">
                  <span className="text-[10px] font-black uppercase text-rose-600 px-3"><AlertTriangle size={14} className="inline mr-1"/> Delete Permanent?</span>
                  <button onClick={() => setIsConfirmingDelete(false)} className="px-4 py-2 bg-white text-slate-500 rounded-xl font-bold text-xs">No</button>
                  <button onClick={() => onDeleteUnit(unit.id)} className="px-6 py-2 bg-rose-600 text-white rounded-xl font-bold text-xs shadow-lg shadow-rose-200">Yes, Delete</button>
                </div>
              ) : (
                <>
                  <button onClick={() => setIsConfirmingDelete(true)} className="px-6 py-3 bg-rose-50 text-rose-600 rounded-2xl font-bold flex items-center gap-2 hover:bg-rose-100 transition-colors">
                    <Trash2 size={18} /> Delete Property
                  </button>
                  <button onClick={() => setIsEditing(true)} className="px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold flex items-center gap-2 hover:bg-blue-700 shadow-xl shadow-blue-100 transition-colors">
                    <Edit2 size={18} /> Edit Unit
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Top Section: Property Info */}
      <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm relative overflow-hidden">
        <div className="flex flex-col md:flex-row gap-8 mb-10">
          <div className="w-40 h-40 bg-slate-50 rounded-[2.5rem] border border-slate-100 flex-shrink-0 relative group overflow-hidden">
            {formData.thumbnail ? <img src={formData.thumbnail} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={48} /></div>}
            {isEditing && <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer"><Camera size={24} className="text-white" /><input type="file" accept="image/*" className="hidden" onChange={handleThumbnailUpload} /></label>}
          </div>
          <div className="flex-1 space-y-6">
            <div className="space-y-2">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase text-blue-600 tracking-[0.2em] mb-1"><MapPin size={12}/> Property Location</div>
                {isEditing ? (
                  <div className="space-y-4">
                    <input placeholder="Street Address" value={formData.address} onChange={(e) => updateField('address', e.target.value)} className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 font-bold" />
                    <div className="grid grid-cols-3 gap-4">
                      <input placeholder="City" value={formData.city} onChange={(e) => updateField('city', e.target.value)} className="col-span-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 font-bold text-sm" />
                      <input placeholder="State" value={formData.state} onChange={(e) => updateField('state', e.target.value)} className="col-span-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 font-bold text-sm" />
                      <input placeholder="Zip Code" value={formData.zip} onChange={(e) => updateField('zip', e.target.value)} className="col-span-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 font-bold text-sm" />
                    </div>
                  </div>
                ) : (
                  <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-none">{formData.address}<br/><span className="text-slate-400 text-lg font-bold">{formData.city}, {formData.state} {formData.zip}</span></h2>
                )}
            </div>
            <div className="flex gap-3">
                {isEditing ? (
                  <select value={formData.status} onChange={(e) => updateField('status', e.target.value as UnitStatus)} className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500">
                    {Object.values(UnitStatus).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : ( <Badge status={formData.status} /> )}
                <Badge status={`${formData.year} ${formData.make}`} />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6 border-t border-slate-50">
          <EditableField isEditing={isEditing} label="Model Year" value={formData.year} icon={Calendar} onChange={(v) => updateField('year', v)} type="number" />
          <EditableField isEditing={isEditing} label="Make" value={formData.make} icon={Settings} onChange={(v) => updateField('make', v)} />
          <EditableField isEditing={isEditing} label="Model" value={formData.model} icon={Database} onChange={(v) => updateField('model', v)} />
          <EditableField isEditing={isEditing} label="Last Inspection" value={formData.lastInspectionDate} icon={ShieldCheck} onChange={(v) => updateField('lastInspectionDate', v)} type="date" />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* Left Column: Tenant & Household */}
        <div className="space-y-8">
          <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm space-y-10">
            <h3 className="text-xl font-bold flex items-center gap-2"><Users size={20} className="text-blue-600" /> Tenant Profile</h3>
            
            {/* Primary Tenant Details */}
            <div className="space-y-6">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                 <EditableField isEditing={isEditing} label="Primary Tenant" value={formData.tenantName} icon={User} onChange={(v) => updateField('tenantName', v)} />
                 <EditableField isEditing={isEditing} label="Monthly Rent" value={formData.monthlyRent} icon={DollarSign} onChange={(v) => updateField('monthlyRent', v)} type="number" />
                 <EditableField isEditing={isEditing} label="Phone Number" value={formData.tenantPhone} icon={Phone} onChange={(v) => updateField('tenantPhone', v)} />
                 <EditableField isEditing={isEditing} label="Email Address" value={formData.tenantEmail} icon={Mail} onChange={(v) => updateField('tenantEmail', v)} />
               </div>
               
               <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Lease Agreement</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <EditableField isEditing={isEditing} label="Move-In Date" value={formData.moveInDate} icon={Calendar} onChange={(v) => updateField('moveInDate', v)} type="date" />
                    <EditableField isEditing={isEditing} label="Lease Start" value={formData.leaseStart} icon={FileText} onChange={(v) => updateField('leaseStart', v)} type="date" />
                    <EditableField isEditing={isEditing} label="Payoff Date" value={formData.leaseEnd} icon={CheckCircle2} onChange={(v) => updateField('leaseEnd', v)} type="date" />
                  </div>
               </div>
            </div>

            {/* Income Total Banner */}
            <div className="bg-blue-600/5 p-5 rounded-2xl border border-blue-100 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-100"><TrendingUp size={20}/></div>
                <div>
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest leading-none mb-1">Total Household Income</p>
                  <p className="text-xs font-bold text-blue-600">Combined from recorded jobs</p>
                </div>
              </div>
              <div className="text-right"><p className="text-3xl font-black text-blue-700 tracking-tight">${totalMonthlyIncome.toLocaleString()}</p></div>
            </div>

            {/* Detailed Demographics */}
            <div className="space-y-6">
                <h4 className="text-sm font-black uppercase text-slate-400 tracking-widest">Additional Occupants</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <EditableField isEditing={isEditing} label="Spouse Name" value={formData.spouseName} icon={User} onChange={(v) => updateField('spouseName', v)} />
                   <EditableField isEditing={isEditing} label="Spouse Phone" value={formData.spousePhone} icon={Phone} onChange={(v) => updateField('spousePhone', v)} />
                </div>
                <EditableField isEditing={isEditing} label="Children Names" value={formData.childrenNames} icon={Users} onChange={(v) => updateField('childrenNames', v)} />
            </div>

            {/* Employment History */}
            <div className="space-y-4">
               <h4 className="text-sm font-black uppercase text-slate-400 tracking-widest">Employment History</h4>
               {formData.employmentHistory.map(job => (
                 <div key={job.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-wrap lg:flex-nowrap items-center gap-6">
                    <div className="flex-1"><EditableField isEditing={isEditing} label="Employer" value={job.jobName} icon={Briefcase} onChange={(v) => updateJob(job.id, 'jobName', v)} /></div>
                    <div className="flex-1"><EditableField isEditing={isEditing} label="Worker" value={job.tenantName} icon={User} onChange={(v) => updateJob(job.id, 'tenantName', v)} /></div>
                    <div className="w-32"><EditableField isEditing={isEditing} label="Monthly Income" value={job.monthlyIncome} icon={DollarSign} onChange={(v) => updateJob(job.id, 'monthlyIncome', v)} type="number" /></div>
                    {isEditing && <button onClick={() => setFormData(prev => ({...prev, employmentHistory: prev.employmentHistory.filter(j => j.id !== job.id)}))} className="text-slate-300 hover:text-rose-500"><Trash2 size={16}/></button>}
                 </div>
               ))}
               {isEditing && <button onClick={handleAddJob} className="text-xs font-bold text-blue-600">+ Add Job Entry</button>}
            </div>

            {/* Driver's License Upload Section */}
            <div className="pt-6 border-t border-slate-50 space-y-6">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Driver's License / ID</p>
              <div className="flex items-center gap-6">
                <div className="w-32 h-20 bg-slate-50 rounded-xl border border-slate-100 overflow-hidden relative group">
                  {formData.driversLicense ? (
                    <img src={formData.driversLicense} className="w-full h-full object-cover" alt="ID scan" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <IdCard size={32} />
                    </div>
                  )}
                  {isEditing && (
                    <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                      <UploadCloud size={16} className="text-white" />
                      <input type="file" accept="image/*" onChange={handleLicenseUpload} className="hidden" />
                    </label>
                  )}
                </div>
                {!isEditing && formData.driversLicense && (
                  <button onClick={() => setViewPhoto(formData.driversLicense!)} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <Maximize2 size={12}/> View Full ID
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Docs, Maintenance, Logs, Tax */}
        <div className="space-y-8">
           
           {/* Document Vault */}
           <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm space-y-8">
             <div className="flex justify-between items-center">
               <h3 className="text-xl font-bold flex items-center gap-2"><FileText size={20} className="text-blue-600" /> Document Vault</h3>
               {isEditing && (
                 <label className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-[10px] font-black uppercase hover:bg-blue-100 cursor-pointer flex items-center gap-2">
                   <UploadCloud size={14}/> Upload PDF
                   <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={handleDocumentUpload} className="hidden" />
                 </label>
               )}
             </div>
             <div className="grid grid-cols-1 gap-3">
                {formData.documents.length === 0 && <p className="text-slate-400 text-sm font-medium italic">No documents uploaded.</p>}
                {formData.documents.map(doc => (
                  <div key={doc.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between group">
                     <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-blue-500 shadow-sm"><FileText size={20}/></div>
                        <div>
                          <p className="text-sm font-bold text-slate-700 truncate max-w-[150px]">{doc.name}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase">{doc.type}</p>
                        </div>
                     </div>
                     <div className="flex items-center gap-2">
                        <a href={doc.data} download={doc.name} className="p-2 bg-white text-slate-500 rounded-lg hover:text-blue-600 shadow-sm"><Download size={16}/></a>
                        {isEditing && <button onClick={() => setFormData(prev => ({...prev, documents: prev.documents.filter(d => d.id !== doc.id)}))} className="p-2 bg-white text-rose-400 rounded-lg hover:text-rose-600 shadow-sm"><Trash2 size={16}/></button>}
                     </div>
                  </div>
                ))}
             </div>
           </div>

           {/* Property Condition / Maintenance */}
           <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm space-y-8">
              <h3 className="text-xl font-bold flex items-center gap-2"><ClipboardList size={20} className="text-blue-600" /> Condition & Maintenance</h3>
              <div className="space-y-3">
                {formData.conditionNotes.map((note, idx) => (
                  <div key={idx} className="flex items-start gap-3 group">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium text-slate-600 leading-relaxed flex-1">{note}</p>
                    {isEditing && <button onClick={() => setFormData(prev => ({...prev, conditionNotes: prev.conditionNotes.filter((_, i) => i !== idx)}))} className="text-slate-300 hover:text-rose-500"><Trash2 size={14}/></button>}
                  </div>
                ))}
                {isEditing && (
                  <div className="flex gap-2 mt-4">
                    <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add maintenance note..." className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-sm outline-none" onKeyDown={(e) => e.key === 'Enter' && handleAddConditionNote()} />
                    <button onClick={handleAddConditionNote} className="px-4 py-2 bg-slate-900 text-white rounded-xl"><Plus size={18}/></button>
                  </div>
                )}
              </div>
           </div>

           {/* Communication Log */}
           <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm space-y-8">
             <div className="flex justify-between items-center"><h3 className="text-xl font-bold flex items-center gap-2"><MessageSquare size={20} className="text-blue-600" /> Communication Log</h3>{isEditing && <button onClick={handleAddLog} className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-[10px] font-black uppercase hover:bg-blue-100">Add Entry</button>}</div>
             <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
               {(formData.communicationLog || []).map((log) => (
                 <div key={log.id} className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4 group">
                   <div className="flex justify-between items-center">
                     <div className="flex items-center gap-3"><Calendar size={14} className="text-slate-400" />{isEditing ? <input type="date" value={log.date} onChange={(e) => updateLog(log.id, 'date', e.target.value)} className="bg-transparent border-none font-bold text-xs outline-none p-0" /> : <span className="text-xs font-bold text-slate-500">{new Date(log.date).toLocaleDateString()}</span>}</div>
                     {isEditing && <button onClick={() => setFormData(prev => ({...prev, communicationLog: prev.communicationLog.filter(l => l.id !== log.id)}))} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100"><Trash2 size={16} /></button>}
                   </div>
                   {isEditing ? <textarea value={log.note} onChange={(e) => updateLog(log.id, 'note', e.target.value)} className="w-full bg-white border border-slate-100 rounded-xl px-4 py-3 text-sm h-24 resize-none" /> : <p className="text-sm font-medium text-slate-700 leading-relaxed whitespace-pre-wrap">{log.note || "No note recorded."}</p>}
                 </div>
               ))}
             </div>
           </div>

           {/* Tax Records */}
           <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm space-y-8">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold flex items-center gap-2"><DollarSign size={20} className="text-blue-600" /> County Tax Records</h3>
              <select value={selectedTaxYear} onChange={(e) => setSelectedTaxYear(Number(e.target.value))} className="bg-slate-50 border border-slate-100 text-xs font-black rounded-xl px-4 py-2 outline-none">
                {taxYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            
            <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Globe size={14} className="text-slate-400" />
                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">County Tax Portal</span>
                </div>
                {isEditing ? (
                    <input 
                        type="url" 
                        placeholder="https://..." 
                        value={formData.taxInfo?.taxWebsite || ''} 
                        onChange={(e) => setFormData(prev => ({
                            ...prev, 
                            taxInfo: { ...(prev.taxInfo || { records: {} }), taxWebsite: e.target.value }
                        }))}
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 font-bold text-sm"
                    />
                ) : (
                    formData.taxInfo?.taxWebsite ? (
                        <a href={formData.taxInfo.taxWebsite} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-600 hover:underline flex items-center gap-1">
                            {formData.taxInfo.taxWebsite} <ArrowUpRight size={12}/>
                        </a>
                    ) : (
                        <span className="text-sm font-bold text-slate-400 italic">No URL recorded</span>
                    )
                )}
            </div>

            <div className="grid grid-cols-1 gap-4">
                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">1st Half Payment</p>
                  <div className="grid grid-cols-2 gap-4">
                    <EditableField isEditing={isEditing} label="Amount" value={currentTaxRecord.payment1.amount} icon={DollarSign} onChange={(v) => updateTaxPayment('payment1', 'amount', v)} type="number" />
                    <EditableField isEditing={isEditing} label="Date" value={currentTaxRecord.payment1.date} icon={Calendar} onChange={(v) => updateTaxPayment('payment1', 'date', v)} type="date" />
                  </div>
                </div>
                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">2nd Half Payment</p>
                  <div className="grid grid-cols-2 gap-4">
                    <EditableField isEditing={isEditing} label="Amount" value={currentTaxRecord.payment2.amount} icon={DollarSign} onChange={(v) => updateTaxPayment('payment2', 'amount', v)} type="number" />
                    <EditableField isEditing={isEditing} label="Date" value={currentTaxRecord.payment2.date} icon={Calendar} onChange={(v) => updateTaxPayment('payment2', 'date', v)} type="date" />
                  </div>
                </div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Photos */}
        <div className="xl:col-span-2 space-y-8">
          <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm">
            <h3 className="text-lg font-bold flex items-center gap-2 mb-6"><Camera size={18} className="text-blue-600" /> Photo Gallery</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {formData.photos.map((p, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 group relative">
                  <img src={p} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2">
                    <button onClick={() => setViewPhoto(p)} className="p-2 bg-white rounded-lg"><Maximize2 size={16} /></button>
                    {isEditing && <button onClick={() => setFormData(prev => ({...prev, photos: prev.photos.filter((_, idx) => idx !== i)}))} className="p-2 bg-rose-500 text-white rounded-lg"><Trash2 size={16} /></button>}
                  </div>
                </div>
              ))}
              {isEditing && <label className="aspect-square border-2 border-dashed border-slate-200 rounded-2xl flex items-center justify-center text-slate-300 hover:bg-slate-50 cursor-pointer"><Plus size={24}/><input type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" /></label>}
            </div>
          </div>
        </div>
      </div>

      {viewPhoto && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm z-[200] flex items-center justify-center p-8 animate-fadeIn" onClick={() => setViewPhoto(null)}>
          <button className="absolute top-8 right-8 text-white"><X size={32} /></button>
          <img src={viewPhoto} className="max-w-5xl max-h-full object-contain rounded-[2rem]" />
        </div>
      )}
    </div>
  );
};

// --- LoginPage Component ---

const LoginPage = ({ onLogin }: { onLogin: (email: string) => void }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f172a] p-6">
      <div className="bg-white rounded-[3rem] p-12 w-full max-w-md shadow-2xl space-y-8 animate-fadeIn">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-white mx-auto shadow-lg shadow-blue-100">
            <Home size={32} />
          </div>
          <h1 className="text-2xl font-black uppercase text-slate-900 tracking-tight leading-tight">Category One<br/>Properties</h1>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Management Portal</p>
        </div>
        <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); onLogin(email); }}>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 flex items-center gap-2">
              <Mail size={12}/> Email Address
            </label>
            <input 
              type="email" 
              required 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 outline-none focus:ring-2 focus:ring-blue-100 font-bold transition-all text-sm"
              placeholder="admin@categoryone.com"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 flex items-center gap-2">
              <Lock size={12}/> Access Password
            </label>
            <input 
              type="password" 
              required 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 outline-none focus:ring-2 focus:ring-blue-100 font-bold transition-all text-sm"
              placeholder="••••••••"
            />
          </div>
          <button 
            type="submit" 
            className="w-full py-5 bg-blue-600 text-white rounded-2xl font-black uppercase text-[10px] shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            Sign In to Dashboard <ArrowRight size={16}/>
          </button>
        </form>
        <div className="pt-6 border-t border-slate-50 text-center">
           <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center justify-center gap-2">
            <ShieldCheck size={14} className="text-emerald-500"/> Secure Cloud Persistence Enabled
          </p>
        </div>
      </div>
    </div>
  );
};

// --- Main App Logic ---

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'units' | 'ledger' | 'analysis' | 'documentation'>('dashboard');
  const [activeDocSubTab, setActiveDocSubTab] = useState<'none' | 'rent-ledger' | 'portfolio'>('none');
  const [isDocExpanded, setIsDocExpanded] = useState(false);
  const [units, setUnits] = useState<Unit[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [paymentFormUnitId, setPaymentFormUnitId] = useState<string>('');
  const [paymentFormAmount, setPaymentFormAmount] = useState<number>(0);
  const [ledgerYearFilter, setLedgerYearFilter] = useState<number>(new Date().getFullYear());
  const [expandedMonths, setExpandedMonths] = useState<Set<number>>(new Set([new Date().getMonth()]));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [aiLoading, setAiLoading] = useState(false);
  const [damageDesc, setDamageDesc] = useState('');
  const [aiResult, setAiResult] = useState<{parts: number, labor: number, justification: string} | null>(null);

  useEffect(() => {
    const initData = async () => {
      const u = await loadUnits();
      const t = await loadTransactions();
      setUnits(u);
      setTransactions(t);
      setIsLoading(false);
    };
    initData();
    const savedProfile = localStorage.getItem('mh_current_profile');
    if (savedProfile) setSession({ user: JSON.parse(savedProfile) });
  }, []);

  const handleLogin = (email: string) => {
    const profile = { email, role: 'Admin', id: 'admin-1', name: email.split('@')[0] };
    localStorage.setItem('mh_current_profile', JSON.stringify(profile));
    setSession({ user: profile });
  };

  const handleLogout = () => { localStorage.removeItem('mh_current_profile'); setSession(null); };

  const handleSaveUnit = async (updatedUnit: Unit) => {
    const exists = units.find(u => u.id === updatedUnit.id);
    const next = exists ? units.map(u => u.id === updatedUnit.id ? updatedUnit : u) : [updatedUnit, ...units];
    setUnits(next);
    await saveUnits(next);
    setSelectedUnit(updatedUnit);
  };

  const handleDeleteUnit = async (id: string) => {
    const next = units.filter(u => u.id !== id);
    setUnits(next);
    await saveUnits(next);
    setSelectedUnit(null);
  };

  const handleAddTransaction = async (data: Partial<Transaction>) => {
    const newTx: Transaction = {
      id: Math.random().toString(36).substr(2, 9), unitId: data.unitId || '',
      amount: data.amount || 0, fee: data.fee || 0, date: data.date || new Date().toISOString(),
      category: (data.category as any) || 'Rent', notes: data.notes
    };
    const next = [...transactions, newTx];
    setTransactions(next);
    await saveTransactions(next);
    setIsAddingPayment(false);
  };

  const handleDeleteTransaction = async (id: string) => {
    if (!confirm("Delete permanently?")) return;
    const next = transactions.filter(t => t.id !== id);
    setTransactions(next);
    await saveTransactions(next);
  };

  const stats = useMemo(() => {
    const totalExpected = units.reduce((acc, u) => acc + (u.status === UnitStatus.OCCUPIED ? u.monthlyRent : 0), 0);
    const thisMonth = new Date().getMonth();
    const currentTxs = transactions.filter(t => {
      const d = new Date(t.date);
      return d.getMonth() === thisMonth && d.getFullYear() === ledgerYearFilter && t.category === 'Rent';
    });
    const totalCollected = currentTxs.reduce((acc, t) => acc + t.amount, 0);
    return { totalExpected, totalCollected, totalUnits: units.length, occupiedUnits: units.filter(u => u.status === UnitStatus.OCCUPIED).length };
  }, [units, transactions, ledgerYearFilter]);

  const groupedTransactions = useMemo(() => {
    const groups: Record<number, { transactions: Transaction[], totalAmount: number, totalFee: number, totalNet: number }> = {};
    transactions.filter(t => new Date(t.date).getFullYear() === ledgerYearFilter).forEach(t => {
      const month = new Date(t.date).getMonth();
      if (!groups[month]) groups[month] = { transactions: [], totalAmount: 0, totalFee: 0, totalNet: 0 };
      groups[month].transactions.push(t);
      groups[month].totalAmount += t.amount;
      groups[month].totalFee += (t.fee || 0);
      groups[month].totalNet += (t.amount - (t.fee || 0));
    });
    return Object.entries(groups).sort(([m1], [m2]) => Number(m2) - Number(m1)).map(([month, data]) => ({ month: Number(month), ...data }));
  }, [transactions, ledgerYearFilter]);

  const totalNetForYear = useMemo(() => transactions.filter(t => new Date(t.date).getFullYear() === ledgerYearFilter).reduce((acc, t) => acc + (t.amount - (t.fee || 0)), 0), [transactions, ledgerYearFilter]);

  const exportToExcel = () => {
    const exportData = transactions.filter(t => new Date(t.date).getFullYear() === ledgerYearFilter).map(t => ({
      'Date Paid': new Date(t.date).toLocaleDateString(),
      'MH / Lot': units.find(u => u.id === t.unitId)?.address || 'Unassigned',
      'Resident': units.find(u => u.id === t.unitId)?.tenantName || 'N/A',
      'Rent Paid': t.amount,
      'Fee': t.fee,
      'Net Received': t.amount - t.fee
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    XLSX.writeFile(wb, `Category_One_Ledger_${ledgerYearFilter}.xlsx`);
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(evt.target?.result as string, { type: 'binary' });
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]) as any[];
        const newTxs = data.map((row, i) => ({
          id: `imp-${Date.now()}-${i}`,
          unitId: units.find(u => u.address.includes(String(row['MH / Lot'])))?.id || '',
          amount: Number(row['Rent Paid'] || 0),
          fee: Number(row['Fee'] || 0),
          date: new Date(row['Date Paid']).toISOString(),
          category: 'Rent' as any
        })).filter(tx => tx.unitId);
        const next = [...transactions, ...newTxs];
        setTransactions(next);
        await saveTransactions(next);
        alert(`Imported ${newTxs.length} records.`);
      } catch (err) { alert('Import Error'); }
    };
    reader.readAsBinaryString(file);
  };

  if (!session) return <LoginPage onLogin={handleLogin} />;
  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-blue-600" size={48}/></div>;

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      <aside className="w-72 bg-[#0f172a] flex flex-col sticky top-0 h-screen">
        <div className="p-8"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white"><Home size={22}/></div><h1 className="font-black text-sm uppercase text-white leading-tight">Category One<br/><span className="text-blue-400">Properties</span></h1></div></div>
        <nav className="px-4 space-y-1 flex-1">
          {[{id:'dashboard',label:'Dashboard',icon:LayoutDashboard},{id:'units',label:'Portfolio',icon:Home},{id:'ledger',label:'Rent Ledger',icon:DollarSign},{id:'analysis',label:'Repair AI',icon:TrendingUp}].map(item => (
            <button key={item.id} onClick={() => { setActiveTab(item.id as any); setSelectedUnit(null); }} className={`w-full flex items-center gap-3 px-6 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === item.id ? 'bg-blue-600 text-white shadow-xl' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}><item.icon size={18} /> {item.label}</button>
          ))}
          <div className="pt-4 mt-4 border-t border-slate-800">
            <button onClick={() => setIsDocExpanded(!isDocExpanded)} className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl font-bold text-sm transition-all ${activeTab === 'documentation' ? 'text-blue-400' : 'text-slate-400 hover:text-white'}`}><div className="flex items-center gap-3"><FileText size={18} /> Documentation</div><ChevronDown size={14} className={isDocExpanded ? 'rotate-180' : ''} /></button>
            {isDocExpanded && ( 
              <div className="ml-10 space-y-1">
                <button onClick={() => { setActiveTab('documentation'); setActiveDocSubTab('portfolio'); }} className={`w-full text-left px-4 py-3 rounded-xl font-bold text-xs transition-all ${activeDocSubTab === 'portfolio' ? 'text-blue-400' : 'text-slate-500 hover:text-white'}`}>Portfolio Doc</button>
                <button onClick={() => { setActiveTab('documentation'); setActiveDocSubTab('rent-ledger'); }} className={`w-full text-left px-4 py-3 rounded-xl font-bold text-xs transition-all ${activeDocSubTab === 'rent-ledger' ? 'text-blue-400' : 'text-slate-500 hover:text-white'}`}>Rent Ledger Doc</button>
              </div> 
            )}
          </div>
        </nav>
      </aside>

      <main className="flex-1 p-12 overflow-y-auto custom-scrollbar">
        {activeTab === 'dashboard' && ( 
          <div className="space-y-8 max-w-6xl mx-auto animate-fadeIn">
            <h2 className="text-3xl font-black tracking-tight">Portfolio Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-1">Target Revenue</p><h3 className="text-3xl font-black">${stats.totalExpected.toLocaleString()}</h3></div>
              <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-1">Total Received</p><h3 className="text-3xl font-black text-emerald-600">${stats.totalCollected.toLocaleString()}</h3></div>
              <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm"><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-1">Occupancy</p><h3 className="text-3xl font-black text-blue-600">{stats.occupiedUnits} / {stats.totalUnits}</h3></div>
            </div>
          </div>
        )}

        {activeTab === 'units' && (
          <div className="max-w-6xl mx-auto animate-fadeIn">
            {selectedUnit ? <UnitDetailView unit={selectedUnit} transactions={transactions} onBack={() => setSelectedUnit(null)} onSave={handleSaveUnit} onDeleteUnit={handleDeleteUnit} onDeleteTransaction={handleDeleteTransaction} /> : (
              <div className="space-y-10">
                <div className="flex justify-between items-center"><h2 className="text-3xl font-black tracking-tight">Portfolio Manager</h2><button onClick={() => setSelectedUnit({ id: `unit-${Date.now()}`, address: 'New Listing', city: 'Sunny Meadows', state: 'FL', zip: '33544', status: UnitStatus.VACANT, monthlyRent: 850, lastInspectionDate: new Date().toISOString().split('T')[0], year: new Date().getFullYear(), make: '', model: '', tenantName: '', tenantPhone: '', tenantEmail: '', spouseName: '', spousePhone: '', spouseEmail: '', childrenNames: '', leaseStart: '', leaseEnd: '', moveInDate: '', salesAmount: 0, employmentHistory: [], conditionNotes: [], communicationLog: [], photos: [], documents: [], taxInfo: { records: {}, taxWebsite: '' } })} className="px-6 py-3 bg-blue-600 text-white rounded-2xl font-black uppercase text-[10px] flex items-center gap-2 shadow-xl shadow-blue-100"><Plus size={18}/> Add Property</button></div>
                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden"><table className="w-full text-left">
                  <thead className="bg-slate-50 border-b border-slate-100"><tr><th className="px-8 py-5 text-[10px] font-black uppercase text-slate-400">Property / Lot</th><th className="px-8 py-5 text-[10px] font-black uppercase text-slate-400">Status</th><th className="px-8 py-5 text-[10px] font-black uppercase text-slate-400">Resident</th><th className="px-8 py-5 text-right text-[10px] font-black uppercase text-slate-400">Monthly Rent</th><th className="px-8 py-5"></th></tr></thead>
                  <tbody className="divide-y divide-slate-50">{units.map(u => (
                    <tr key={u.id} onClick={() => setSelectedUnit(u)} className="group hover:bg-slate-50 cursor-pointer"><td className="px-8 py-6 font-black">{u.address}</td><td className="px-8 py-6"><Badge status={u.status} /></td><td className="px-8 py-6 text-sm font-bold">{u.tenantName || 'Vacant'}</td><td className="px-8 py-6 text-right font-black">${u.monthlyRent}</td><td className="px-8 py-6 text-center"><ChevronRight size={20} className="text-slate-300 group-hover:text-blue-600 transition-colors" /></td></tr>
                  ))}</tbody></table></div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ledger' && (
          <div className="animate-fadeIn space-y-8 max-w-7xl mx-auto">
             <div className="flex justify-between items-center">
               <div><h2 className="text-3xl font-black tracking-tight">Rent Ledger - {ledgerYearFilter}</h2><p className="text-xs font-bold text-slate-400 mt-1 uppercase tracking-widest">Yearly Net: <span className="text-emerald-600">${totalNetForYear.toLocaleString()}</span></p></div>
               <div className="flex gap-3">
                 <select value={ledgerYearFilter} onChange={(e)=>setLedgerYearFilter(Number(e.target.value))} className="bg-white border p-2 rounded-xl text-xs font-black"><option value={2026}>2026</option><option value={2025}>2025</option><option value={2024}>2024</option></select>
                 <button onClick={exportToExcel} className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-[10px] uppercase flex items-center gap-2 hover:bg-slate-50"><Download size={14} /> Export List</button>
                 <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-[10px] uppercase flex items-center gap-2 hover:bg-slate-50"><UploadCloud size={14} /> Import Data</button>
                 <input type="file" ref={fileInputRef} onChange={handleImportExcel} className="hidden" accept=".xlsx" />
                 <button onClick={()=>setIsAddingPayment(true)} className="bg-blue-600 text-white px-6 py-3 rounded-2xl font-black uppercase text-[10px] shadow-xl shadow-blue-100">+ Record Receipt</button>
               </div>
             </div>
             <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-slate-50 bg-slate-50/30 flex items-center gap-3"><History size={18} className="text-blue-600" /><h3 className="font-black text-xs uppercase tracking-widest text-slate-500">Transaction History: {ledgerYearFilter}</h3></div>
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-[10px] uppercase font-black text-slate-400 border-b border-slate-50"><tr><th className="px-8 py-5">Date Paid</th><th className="px-8 py-5">MH / Lot</th><th className="px-8 py-5">Payee (Resident)</th><th className="px-8 py-5 text-right">Rent Paid</th><th className="px-8 py-5 text-right text-rose-500">PayPal Fee</th><th className="px-8 py-5 text-right bg-emerald-50/20">Net Received</th><th className="px-8 py-5 text-center">Actions</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">{groupedTransactions.map(group => {
                    const monthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2024, group.month, 1));
                    const isExpanded = expandedMonths.has(group.month);
                    return (
                      <React.Fragment key={group.month}>
                        <tr onClick={() => { setExpandedMonths(prev => { const n = new Set(prev); if (n.has(group.month)) n.delete(group.month); else n.add(group.month); return n; }); }} className="bg-slate-50/30 cursor-pointer hover:bg-slate-100/50">
                          <td colSpan={2} className="px-8 py-4"><div className="flex items-center gap-3">{isExpanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}<span className="font-black text-sm uppercase text-slate-600">{monthName}</span></div></td>
                          <td className="px-8 py-4"></td>
                          <td className="px-8 py-4 text-right font-black text-sm">${group.totalAmount.toLocaleString()}</td>
                          <td className="px-8 py-4 text-right font-bold text-rose-500 text-xs">-${group.totalFee.toLocaleString()}</td>
                          <td className="px-8 py-4 text-right bg-emerald-50/20 font-black text-emerald-700 text-sm">${group.totalNet.toLocaleString()}</td>
                          <td className="px-8 py-4"></td>
                        </tr>
                        {isExpanded && group.transactions.map(t => (
                          <tr key={t.id} className="animate-fadeIn"><td className="px-8 py-4 pl-14 text-xs font-bold text-slate-500">{new Date(t.date).toLocaleDateString()}</td><td className="px-8 py-4 text-xs font-bold">{units.find(u=>u.id===t.unitId)?.address}</td><td className="px-8 py-4 text-xs font-bold text-slate-600">{units.find(u=>u.id===t.unitId)?.tenantName || 'N/A'}</td><td className="px-8 py-4 text-right text-xs font-black">${t.amount.toLocaleString()}</td><td className="px-8 py-4 text-right text-xs font-bold text-rose-500">-${(t.fee || 0).toLocaleString()}</td><td className="px-8 py-4 text-right font-black text-emerald-600 text-xs bg-emerald-50/10">${(t.amount - (t.fee || 0)).toLocaleString()}</td><td className="px-8 py-4 text-center"><button onClick={()=>handleDeleteTransaction(t.id)} className="p-1.5 text-slate-200 hover:text-rose-500"><Trash2 size={14}/></button></td></tr>
                        ))}
                      </React.Fragment>
                    );
                  })}</tbody>
                </table>
             </div>
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="animate-fadeIn space-y-8 max-w-2xl mx-auto">
            <h2 className="text-3xl font-black tracking-tight">Repair AI Estimator</h2>
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 space-y-6 shadow-sm">
              <div className="space-y-2"><label className="text-xs font-black uppercase text-slate-400 tracking-widest">Problem Description</label><textarea value={damageDesc} onChange={(e) => setDamageDesc(e.target.value)} placeholder="e.g. Broken sky-light in living room, standard 2x2. Describe the repair needed..." className="w-full h-40 bg-slate-50 border border-slate-100 rounded-2xl p-6 outline-none focus:ring-2 focus:ring-blue-100 text-sm font-medium transition-all" /></div>
              <button onClick={async () => { setAiLoading(true); try { const res = await getAIEstimate(damageDesc); setAiResult(res); } catch (e) { alert('AI Error'); } finally { setAiLoading(false); } }} disabled={aiLoading || !damageDesc} className="w-full py-5 bg-blue-600 text-white rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-3 hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 disabled:opacity-50">{aiLoading ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />} Generate Professional Estimate</button>
              {aiResult && ( <div className="p-8 bg-slate-50 rounded-2xl border border-slate-100 space-y-4 animate-fadeIn"><div className="grid grid-cols-2 gap-4"><div className="bg-white p-5 rounded-2xl shadow-sm"><p className="text-[10px] uppercase font-black text-slate-400 tracking-widest">Material Costs</p><p className="text-2xl font-black text-slate-900 mt-1">${aiResult.parts.toLocaleString()}</p></div><div className="bg-white p-5 rounded-2xl shadow-sm"><p className="text-[10px] uppercase font-black text-slate-400 tracking-widest">Labor (est.)</p><p className="text-2xl font-black text-slate-900 mt-1">${aiResult.labor.toLocaleString()}</p></div></div><div className="bg-white/50 p-6 rounded-2xl border border-slate-100"><p className="text-xs text-slate-600 italic leading-relaxed font-medium">"{aiResult.justification}"</p></div></div> )}
            </div>
          </div>
        )}

        {activeTab === 'documentation' && ( <DocumentationView activeDoc={activeDocSubTab} /> )}
      </main>

      {isAddingPayment && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-lg overflow-hidden border border-white">
            <div className="p-8 bg-[#0f172a] text-white flex justify-between items-center"><div className="flex items-center gap-3"><Receipt size={24} className="text-blue-400"/><h3 className="font-black uppercase text-sm tracking-widest">Record Rent Receipt</h3></div><button onClick={() => setIsAddingPayment(false)} className="hover:text-rose-400 transition-colors"><X size={24}/></button></div>
            <form className="p-10 space-y-6" onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); handleAddTransaction({ unitId: fd.get('unitId') as string, amount: Number(fd.get('amount')), fee: Number(fd.get('fee')), date: fd.get('date') as string, notes: fd.get('notes') as string, category: 'Rent' }); }}>
              <div className="space-y-4">
                <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Property Assignment (Lot #)</label><select name="unitId" defaultValue={paymentFormUnitId} className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-sm focus:ring-2 focus:ring-blue-100">{units.map(u => <option key={u.id} value={u.id}>{u.address} ({u.tenantName || 'Vacant'})</option>)}</select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Gross Rent Paid</label><input name="amount" type="number" step="0.01" defaultValue={paymentFormAmount || ''} required className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-xl font-black outline-none focus:ring-2 focus:ring-blue-100" /></div>
                  <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Deduction (Fee)</label><input name="fee" type="number" step="0.01" defaultValue="0" required className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-xl font-black outline-none text-rose-500 focus:ring-2 focus:ring-rose-100" /></div>
                </div>
                <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Payment Date</label><input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold outline-none focus:ring-2 focus:ring-blue-100" /></div>
                <button type="submit" className="w-full py-5 bg-blue-600 text-white rounded-[1.5rem] font-black uppercase text-[10px] shadow-2xl hover:bg-blue-700 transition-all active:scale-[0.98]">Post Transaction to Ledger</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
